import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  PlusCircle, MoreHorizontal, Send, Copy, Ban, IndianRupee,
  ExternalLink, Receipt, Loader2, Plus, Repeat,
} from 'lucide-react';

// ────────────────────────── constants

const STATUS_STYLE = {
  draft:      { label: 'Draft',      cls: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
  link_sent:  { label: 'Link Sent',  cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  paid:       { label: 'Paid',       cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  cancelled:  { label: 'Cancelled',  cls: 'bg-red-500/10 text-red-400 border-red-500/30 line-through' },
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// ────────────────────────── AddonChargeDialog

/**
 * Create OR edit an add-on charge. Edit is only allowed while status
 * === 'draft' (enforced server-side too).
 */
const AddonChargeDialog = ({ open, onOpenChange, lead, editingAddon, onSaved }) => {
  const [form, setForm] = useState({
    description: '', amount: '', extend_revisions: false, notes: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingAddon) {
      setForm({
        description: editingAddon.description || '',
        amount: String(editingAddon.amount ?? ''),
        extend_revisions: !!editingAddon.extend_revisions,
        notes: editingAddon.notes || '',
      });
    } else {
      setForm({ description: '', amount: '', extend_revisions: false, notes: '' });
    }
  }, [open, editingAddon]);

  const submit = async () => {
    const desc = form.description.trim();
    if (!desc) { toast.error('Description is required'); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setBusy(true);
    try {
      const payload = {
        description: desc,
        amount: amt,
        extend_revisions: form.extend_revisions,
        notes: form.notes.trim(),
      };
      const r = editingAddon
        ? await api.patch(`/smb/addons/${editingAddon.id}`, payload)
        : await api.post(`/smb/leads/${lead.id}/addons`, payload);
      if (r.data?.success) {
        toast.success(editingAddon ? 'Add-on updated' : 'Add-on created');
        onSaved(r.data.data);
        onOpenChange(false);
      } else {
        toast.error(r.data?.error || 'Failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="addon-charge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-500" />
            {editingAddon ? 'Edit Add-on Charge' : 'Add Customization Charge'}
          </DialogTitle>
          <DialogDescription>
            Log an incremental charge (extra section, custom feature, etc.).
            You&apos;ll send the payment link separately after creating it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ac-desc" className="text-xs">Description *</Label>
            <Input
              id="ac-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={busy}
              placeholder="e.g. Extra 'Testimonials' section"
              maxLength={500}
              data-testid="ac-description"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ac-amt" className="text-xs">Amount (₹, GST inclusive) *</Label>
            <Input
              id="ac-amt"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              disabled={busy}
              placeholder="e.g. 999"
              data-testid="ac-amount"
            />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox
              checked={form.extend_revisions}
              onCheckedChange={(v) => setForm({ ...form, extend_revisions: !!v })}
              disabled={busy}
              data-testid="ac-extend-revisions"
            />
            <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Grant +1 additional revision round</span>
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="ac-notes" className="text-xs">Internal notes (optional)</Label>
            <Textarea
              id="ac-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              disabled={busy}
              rows={2}
              placeholder="Context for accounts / design team"
              data-testid="ac-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="ac-submit">
            {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : (editingAddon ? 'Save' : 'Create Add-on')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────── AddonsPanel

/**
 * Add-ons & Customizations card — shown on the SMB Lead Detail page.
 * Lists all add-on charges + provides Send Link / Copy / Cancel actions.
 */
export const AddonsPanel = ({ lead, onManualPay }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_billed: 0, total_pending: 0, count_paid: 0, count_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendingLinkId, setSendingLinkId] = useState(null);

  const fetchAddons = useCallback(async () => {
    if (!lead?.id) return;
    try {
      const r = await api.get(`/smb/leads/${lead.id}/addons`);
      setRows(r.data?.data || []);
      setSummary(r.data?.summary || { total_billed: 0, total_pending: 0, count_paid: 0, count_pending: 0 });
    } catch {
      // silent — no add-ons collection yet is fine
    } finally {
      setLoading(false);
    }
  }, [lead?.id]);

  useEffect(() => { fetchAddons(); }, [fetchAddons]);

  const sendLink = async (addon) => {
    setSendingLinkId(addon.id);
    try {
      const r = await api.post(`/smb/addons/${addon.id}/send-payment-link`);
      if (r.data?.success) {
        toast.success('Razorpay link ready — copy & send via WhatsApp');
        await fetchAddons();
        // Auto-copy for convenience
        try { await navigator.clipboard.writeText(r.data.data.payment_url); }
        catch { /* clipboard may be blocked — user can copy from row */ }
      } else {
        toast.error(r.data?.error || 'Failed to generate link');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSendingLinkId(null);
    }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('Copied to clipboard'); }
    catch { toast.error('Clipboard blocked — long-press the link'); }
  };

  const cancelAddon = async (addon) => {
    const reason = window.prompt(
      `Cancel add-on "${addon.description.slice(0, 50)}"? Optional reason:`, '',
    );
    if (reason === null) return;
    try {
      const r = await api.post(`/smb/addons/${addon.id}/cancel`, { reason });
      if (r.data?.success) { toast.success('Add-on cancelled'); fetchAddons(); }
      else toast.error(r.data?.error || 'Failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <Card className="mb-4" data-testid="addons-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <IndianRupee className="h-4 w-4 text-emerald-500" />
          Add-ons & Customizations
          {rows.length > 0 && (
            <span className="text-xs text-muted-foreground font-normal">
              · {summary.count_paid} paid ({fmtINR(summary.total_billed)}), {summary.count_pending} pending ({fmtINR(summary.total_pending)})
            </span>
          )}
        </CardTitle>
        <Button
          size="sm" variant="outline"
          onClick={() => { setEditing(null); setCreateOpen(true); }}
          data-testid="add-addon-btn"
          className="border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
        >
          <PlusCircle className="h-3.5 w-3.5 mr-1" />
          Add Charge
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="text-xs text-muted-foreground py-3">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-3">
            No add-on charges yet. Click <strong>Add Charge</strong> to log a customization.
          </div>
        ) : (
          <ul className="divide-y divide-border/50">
            {rows.map((r) => {
              const style = STATUS_STYLE[r.status] || STATUS_STYLE.draft;
              return (
                <li key={r.id} className="py-2.5 flex items-start gap-3" data-testid={`addon-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{r.description}</span>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${style.cls}`}>
                        {style.label}
                      </Badge>
                      {r.extend_revisions && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-500/10 text-blue-400 border-blue-500/30">
                          +1 revision
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-emerald-400">{fmtINR(r.amount)}</span>
                      {r.tax_invoice_number && (
                        <span className="flex items-center gap-1">
                          <Receipt className="h-3 w-3" />
                          <span className="font-mono">{r.tax_invoice_number}</span>
                        </span>
                      )}
                      {r.paid_at && <span>· Paid {new Date(r.paid_at).toLocaleDateString()}</span>}
                      {r.payment_url && r.status === 'link_sent' && (
                        <a href={r.payment_url} target="_blank" rel="noreferrer"
                           className="flex items-center gap-1 text-amber-400 hover:underline">
                          <ExternalLink className="h-3 w-3" />
                          Open link
                        </a>
                      )}
                    </div>
                    {r.notes && <div className="text-[11px] text-muted-foreground/70 mt-0.5 italic">{r.notes}</div>}
                  </div>
                  <div className="flex-shrink-0">
                    {r.status === 'draft' && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => sendLink(r)}
                        disabled={sendingLinkId === r.id}
                        data-testid={`send-link-${r.id}`}
                        className="text-xs mr-1"
                      >
                        {sendingLinkId === r.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <><Send className="h-3 w-3 mr-1" />Send Link</>}
                      </Button>
                    )}
                    {r.status === 'link_sent' && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => copyLink(r.payment_url)}
                        data-testid={`copy-link-${r.id}`}
                        className="text-xs mr-1"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                data-testid={`addon-menu-${r.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {r.status === 'draft' && (
                          <DropdownMenuItem onClick={() => { setEditing(r); setCreateOpen(true); }}>
                            Edit
                          </DropdownMenuItem>
                        )}
                        {(r.status === 'draft' || r.status === 'link_sent') && (
                          <DropdownMenuItem
                            onClick={() => onManualPay && onManualPay(r)}
                            data-testid={`addon-manual-pay-${r.id}`}
                          >
                            Record manual payment
                          </DropdownMenuItem>
                        )}
                        {(r.status === 'draft' || r.status === 'link_sent') && (
                          <DropdownMenuItem
                            onClick={() => cancelAddon(r)}
                            className="text-red-500 focus:text-red-500"
                            data-testid={`addon-cancel-${r.id}`}
                          >
                            <Ban className="h-3.5 w-3.5 mr-1.5" />
                            Cancel
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <AddonChargeDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        lead={lead}
        editingAddon={editing}
        onSaved={fetchAddons}
      />
    </Card>
  );
};

export default AddonsPanel;
